import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SwitchShop } from '../admin/apiServices/ShopApiService'
import { getShops } from './activeShop'

/**
 * "Which shop are you working in today?"
 *
 * WHY THIS BLOCKS THE WHOLE APP RATHER THAN SITTING IN A CORNER
 *
 * A user entitled to two or more shops who has not chosen one is in a state
 * where the application does not really work:
 *
 *   - every WRITE is refused with 422 ("send X-Shop-Id to say which one this is
 *     for"), so no order, payment, purchase or return can be saved;
 *   - their permissions are the INTERSECTION across their shops, so screens
 *     they are perfectly entitled to in one branch are missing.
 *
 * Before this screen existed they landed on the dashboard in that state, and
 * the only clue was a dropdown in the header reading "Choose a shop…". A
 * salesperson does not read the header — they open the sales screen, fill in an
 * order, and discover the problem when saving fails. So the question is asked
 * first, once, and the app proceeds normally afterwards.
 *
 * A user with exactly one shop never sees this: there is no decision to make,
 * and offering one would imply another shop exists somewhere.
 *
 * It doubles as a shift check on a shared counter PC — the first screen after
 * logging in states, in the user's own words, which branch they are billing to.
 */
export default function ShopChooser() {
    const { t } = useTranslation()
    const shops = getShops()
    const [busy, setBusy] = useState(false)
    const [failed, setFailed] = useState(false)

    const choose = async (shopId) => {
        setBusy(true)
        setFailed(false)

        // SwitchShop loads that shop's permissions BEFORE we reload — see the
        // note there. Reloading first would enter the shop holding the previous
        // (here: intersection) permission set.
        const ok = await SwitchShop(shopId)

        if (!ok) {
            setBusy(false)
            setFailed(true)
            return
        }

        window.location.reload()
    }

    return (
        <div className="shop-chooser">
            <div className="shop-chooser__card">
                <h1 className="shop-chooser__title">
                    {t('shop.chooseTitle', { defaultValue: 'Which shop are you working in today?' })}
                </h1>
                <p className="shop-chooser__hint">
                    {t('shop.chooseHint', {
                        defaultValue:
                            'Your orders and payments will be recorded against the shop you choose. You can change it later from the header.',
                    })}
                </p>

                {failed && (
                    <p className="shop-chooser__error" role="alert">
                        {t('shop.switchFailed', {
                            defaultValue: 'Could not switch shop. Please check your connection and try again.',
                        })}
                    </p>
                )}

                <ul className="shop-chooser__list">
                    {shops.map((shop) => (
                        <li key={shop.id}>
                            <button
                                type="button"
                                className="shop-chooser__option ui-focusable"
                                disabled={busy}
                                onClick={() => choose(shop.id)}
                            >
                                {/* Code first: two branches of one business
                                    usually share most of their name ("Dhaka
                                    Main", "Dhaka Uttara"), and the code is the
                                    part that differs at a glance. */}
                                <span className="shop-chooser__code">{shop.code}</span>
                                <span className="shop-chooser__name">{shop.name}</span>
                                {/* The role they hold THERE. The same person is
                                    a Manager in one branch and a Cashier in
                                    another, and this is the only thing that
                                    explains why the menu differs. */}
                                {shop.role && (
                                    <span className="shop-chooser__role">{shop.role}</span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}
